import { Path, PathValue, UseFormReturn } from "react-hook-form";

import { misc } from "@/lib/misc";
import {
  CompensationType,
  Job,
  Requisition,
  RequisitionStatus,
  RevenueType,
} from "@/types";

const setFormValue = <T extends Record<string, unknown>>(
  form: UseFormReturn<T>,
  path: string,
  value: string | number | null,
) => {
  form.setValue(path as Path<T>, value as PathValue<T, Path<T>>, {
    shouldValidate: true,
  });
};

export const handleFunctions = {
  handleCompensationChange: <T extends Record<string, unknown>>({
    form,
    job,
    requisition,
  }: {
    form: UseFormReturn<T>;
    job: Job;
    requisition?: Requisition;
  }) => {
    if (
      job.revenueType === RevenueType.FIXED ||
      requisition?.status === RequisitionStatus.BACKFILL
    )
      return;
    const baseCompensation = form.getValues("baseCompensation" as Path<T>);
    const revenue = misc.calculateEstimatedRevenueAmount(
      job.compensationType === CompensationType.HOURLY,
      Number(baseCompensation),
      Number(baseCompensation),
      job.percentOfSalary ?? 0,
    );
    setFormValue(form, "revenueEarned", revenue);
  },

  handleRequisitionChange: <T extends Record<string, unknown>>({
    form,
    job,
    requisition,
    setSelectedRequisition,
  }: {
    form: UseFormReturn<T>;
    job: Job;
    requisition?: Requisition;
    setSelectedRequisition: (requisition?: Requisition) => void;
  }) => {
    setSelectedRequisition(requisition);
    if (!requisition) return;

    setFormValue(form, "ownerUserId", requisition.requisitionOwners[0].userId);

    setFormValue(
      form,
      "baseCompensation",
      (job.compensationMin + job.compensationMax) / 2,
    );

    if (requisition.status === RequisitionStatus.BACKFILL) {
      setFormValue(form, "revenueEarned", null);
    } else if (job.revenueType === RevenueType.PERCENT && job.percentOfSalary) {
      setFormValue(
        form,
        "revenueEarned",
        misc.calculateEstimatedRevenueAmount(
          job.compensationType === CompensationType.HOURLY,
          job.compensationMin,
          job.compensationMax,
          job.percentOfSalary,
        ),
      );
    } else if (job.revenueType === RevenueType.FIXED) {
      setFormValue(form, "revenueEarned", job.estimatedRevenueAmount);
    }

    setFormValue(form, "retentionBonusDays", requisition.retentionBonusDays);

    setFormValue(
      form,
      "retentionBonusAmount",
      requisition.retentionBonusAmount,
    );

    setFormValue(
      form,
      "shiftTypeDescription",
      requisition.shiftTypeDescription,
    );
  },
};
